import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bookmark, MapPin, Briefcase, Building2, ArrowRight, X } from "lucide-react";

export default function SeekerSavedJobs() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => { (async () => {
        try { const { data } = await api.get("/seeker/saved-jobs"); setJobs(data || []); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    })(); }, []);

    const unsave = async (jid) => {
        try { await api.delete(`/seeker/saved-jobs/${jid}`); setJobs((arr) => arr.filter((j) => j.id !== jid)); toast.success("Removed from saved"); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="max-w-6xl mx-auto p-3 sm:p-6 lg:p-8 space-y-8">
            <div>
                <div className="text-xs uppercase tracking-widest font-mono text-ikb">Bookmarks</div>
                <h1 className="font-display font-black text-2xl sm:text-4xl mt-2 flex items-center gap-3">
                    <Bookmark className="w-8 h-8 text-ikb" /> Saved jobs
                </h1>
                <p className="text-foreground/80 mt-2">Roles you bookmarked, ready when you are.</p>
            </div>
            {loading ? (
                <div className="text-sm font-mono text-foreground/80">Loading…</div>
            ) : jobs.length === 0 ? (
                <div className="border border-dashed border-border p-16 text-center bg-white">
                    <h3 className="font-display font-bold text-xl">Nothing saved yet</h3>
                    <p className="text-sm text-foreground/80 mt-2 mb-6">Tap the bookmark on any job to keep it here.</p>
                    <Link to="/seeker/jobs" className="text-xs font-mono uppercase tracking-widest border border-ikb text-ikb px-4 py-2 hover:bg-ikb hover:text-white inline-block transition-colors">Browse jobs →</Link>
                </div>
            ) : (
                <div className="border border-border bg-white divide-y divide-border">
                    {jobs.map((j) => (
                        <div key={j.id} className="p-5 flex items-start justify-between gap-4 flex-wrap" data-testid={`saved-job-${j.id}`}>
                            <Link to={`/j/${j.slug}`} className="flex-1 min-w-0 group">
                                <div className="font-display font-bold text-lg group-hover:text-ikb transition-colors">{j.title}</div>
                                <div className="flex items-center gap-3 text-xs font-mono text-foreground/80 mt-1 flex-wrap">
                                    <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {j.company?.name || "Company"}</span>
                                    <span>·</span>
                                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {j.location || "Remote"}</span>
                                    <span>·</span>
                                    <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {j.seniority || "—"}</span>
                                </div>
                            </Link>
                            <div className="flex items-center gap-2">
                                <Link to={`/j/${j.slug}`} className="text-xs font-mono uppercase tracking-widest border border-ikb text-ikb px-3 py-1.5 hover:bg-ikb hover:text-white transition-colors inline-flex items-center gap-1">
                                    Apply <ArrowRight className="w-3 h-3" />
                                </Link>
                                <button type="button" onClick={() => unsave(j.id)} className="text-xs font-mono uppercase tracking-widest border border-border px-3 py-1.5 hover:bg-secondary inline-flex items-center gap-1" data-testid={`unsave-${j.id}`}>
                                    <X className="w-3 h-3" /> Remove
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
